import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { Book } from '../../types/Book';
import { bookService } from '../../services/bookService';
import PageHeader from '../common/PageHeader';
import './BookDetail.css';

interface BookDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const BookDetail: React.FC<BookDetailProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  
  // Reading records state
  const [readingRecords, setReadingRecords] = useState<any[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [submittingRecord, setSubmittingRecord] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'by-date'>('list');
  const [activeTab, setActiveTab] = useState<'progress' | 'records' | 'notes'>('progress');
  
  // Reading record form state
  const [recordDate, setRecordDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [recordPagesRead, setRecordPagesRead] = useState<number>(0);
  const [recordStartPage, setRecordStartPage] = useState<number | ''>('');
  const [recordEndPage, setRecordEndPage] = useState<number | ''>('');
  const [recordTimeMinutes, setRecordTimeMinutes] = useState<number | ''>('');
  const [recordNotes, setRecordNotes] = useState<string>('');

  // Form state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [readingStatus, setReadingStatus] = useState<'not_started' | 'reading' | 'completed' | 'paused' | 'abandoned'>('not_started');

  // Load book data
  const loadBook = useCallback(async () => {
    if (!bookId) return;

    setLoading(true);
    setError(null);

    try {
      const bookData = await bookService.getBookById(bookId);
      if (bookData) {
        setBook(bookData);
        setCurrentPage(bookData.currentPage || 0);
        setTotalPages(bookData.totalPages || bookData.pageCount || 0);
        setReadingStatus(bookData.readingStatus || 'not_started');
      } else {
        setError('Book not found');
      }
    } catch (err: any) {
      console.error('Error loading book:', err);
      setError('Failed to load book');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Load reading records
  const loadReadingRecords = useCallback(async () => {
    if (!bookId || !user) return;

    setLoadingRecords(true);
    setError(null);

    try {
      const records = await bookService.getReadingRecords(bookId, {
        limit: 100,
        orderBy: 'record_date'
      });
      setReadingRecords(records);
    } catch (err: any) {
      console.error('Error loading reading records:', err);
      // Don't set error for records, just log it
    } finally {
      setLoadingRecords(false);
    }
  }, [bookId, user]);

  useEffect(() => {
    if (bookId) {
      loadBook();
      loadReadingRecords();
    }
  }, [bookId, loadBook, loadReadingRecords]);

  // Update document title when book loads
  useEffect(() => {
    if (book) {
      document.title = `${book.title} - Book Details`;
    } else {
      document.title = 'Book Details';
    }
    
    // Cleanup: reset title when component unmounts
    return () => {
      document.title = 'Memory Land';
    };
  }, [book]);

  // Save reading progress
  const saveProgress = useCallback(async () => {
    if (!bookId || saving) return;

    setSaving(true);
    setError(null);

    try {
      const success = await bookService.updateReadingProgress(bookId, {
        currentPage: currentPage >= 0 ? currentPage : undefined,
        totalPages: totalPages > 0 ? totalPages : undefined,
        status: readingStatus
      });

      if (success) {
        // Reload book to get updated data
        await loadBook();
      } else {
        setError('Failed to save progress');
      }
    } catch (err: any) {
      console.error('Error saving progress:', err);
      setError('Failed to save progress');
    } finally {
      setSaving(false);
    }
  }, [bookId, currentPage, totalPages, readingStatus, saving, loadBook]);

  // Auto-save on change
  useEffect(() => {
    if (book && !loading) {
      const timer = setTimeout(() => {
        if (
          (currentPage !== (book.currentPage || 0)) ||
          (totalPages !== (book.totalPages || book.pageCount || 0)) ||
          (readingStatus !== (book.readingStatus || 'not_started'))
        ) {
          saveProgress();
        }
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timer);
    }
  }, [currentPage, totalPages, readingStatus, book, loading, saveProgress]);

  // Quick page adjustment
  const adjustPage = (delta: number) => {
    const newPage = Math.max(0, Math.min(currentPage + delta, totalPages || Infinity));
    setCurrentPage(newPage);
  };

  // Handle view notes
  const handleViewNotes = async () => {
    if (!user || !book || isCreatingNote) return;

    setIsCreatingNote(true);
    try {
      // Check if note already exists
      let noteId = await bookService.getBookNoteId(book.id);

      // If note doesn't exist, create it
      if (!noteId) {
        noteId = await bookService.createBookNote(book.id, user.id, book.title);
      }

      // Navigate to note
      if (noteId) {
        navigate(`/notes/${noteId}`);
      }
    } catch (error) {
      console.error('Error accessing book notes:', error);
      setError('Failed to access notes');
    } finally {
      setIsCreatingNote(false);
    }
  };

  // Calculate progress percentage
  const progressPercentage = totalPages > 0 && currentPage > 0
    ? Math.min((currentPage / totalPages) * 100, 100)
    : 0;

  // Handle add reading record
  const handleAddReadingRecord = async () => {
    if (!bookId || submittingRecord) return;

    // Validate pages read
    if (recordPagesRead <= 0) {
      setError('Please enter the number of pages read');
      return;
    }

    setSubmittingRecord(true);
    setError(null);

    try {
      const record = await bookService.createReadingRecord(bookId, {
        record_date: recordDate,
        pages_read: recordPagesRead,
        start_page: recordStartPage ? Number(recordStartPage) : undefined,
        end_page: recordEndPage ? Number(recordEndPage) : undefined,
        reading_time_minutes: recordTimeMinutes ? Number(recordTimeMinutes) : undefined,
        notes: recordNotes || undefined
      });

      if (record) {
        // Reset form
        setRecordDate(new Date().toISOString().split('T')[0]);
        setRecordPagesRead(0);
        setRecordStartPage('');
        setRecordEndPage('');
        setRecordTimeMinutes('');
        setRecordNotes('');
        setShowRecordForm(false);
        
        // Reload records
        await loadReadingRecords();
      } else {
        setError('Failed to create reading record');
      }
    } catch (err: any) {
      console.error('Error creating reading record:', err);
      setError('Failed to create reading record');
    } finally {
      setSubmittingRecord(false);
    }
  };

  // Handle delete reading record
  const handleDeleteRecord = async (recordId: number) => {
    if (!bookId || !window.confirm('Are you sure you want to delete this reading record?')) return;

    try {
      const success = await bookService.deleteReadingRecord(bookId, recordId);
      if (success) {
        await loadReadingRecords();
      } else {
        setError('Failed to delete reading record');
      }
    } catch (err: any) {
      console.error('Error deleting reading record:', err);
      setError('Failed to delete reading record');
    }
  };

  // Get reading records by date
  const readingRecordsByDate = React.useMemo(() => {
    if (viewMode !== 'by-date') return [];
    
    const grouped = readingRecords.reduce((acc: any, record: any) => {
      const date = record.record_date;
      if (!acc[date]) {
        acc[date] = {
          date,
          total_pages: 0,
          session_count: 0,
          total_minutes: 0,
          records: []
        };
      }
      acc[date].total_pages += record.pages_read || 0;
      acc[date].session_count += 1;
      acc[date].total_minutes += record.reading_time_minutes || 0;
      acc[date].records.push(record);
      return acc;
    }, {});

    return Object.values(grouped).sort((a: any, b: any) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [readingRecords, viewMode]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Generate cover URL
  const gutenbergCoverUrl = book ? `https://www.gutenberg.org/cache/epub/${book.id}/pg${book.id}.cover.medium.jpg` : '';
  const coverUrl = book?.coverUrl || gutenbergCoverUrl;

  if (!user) {
    return (
      <div className="book-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Book Details</h1>}
        />
        <div className="book-detail-auth-message">
          Please sign in to access book details.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="book-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="book-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading book details...</p>
        </div>
      </div>
    );
  }

  if (error && !book) {
    return (
      <div className="book-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="book-detail-error">
          <p>{error}</p>
          <button onClick={loadBook} className="book-detail-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="book-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Book Not Found</h1>}
        />
        <div className="book-detail-error">
          <p>The book you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/books')} className="book-detail-retry-btn">Back to Books</button>
        </div>
      </div>
    );
  }

  return (
    <div className="book-detail-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{book.title}</h1>}
        rightActions={
          saving && (
            <span className="book-detail-saving-indicator">
              <div className="saving-spinner"></div>
              Saving...
            </span>
          )
        }
      />

      <div className="book-detail-content">
        {/* Book Info Section - Always Visible */}
        <div className="book-detail-info-section">
          <div className="book-detail-cover-container">
            <img 
              src={coverUrl} 
              alt={`${book.title} cover`}
              className="book-detail-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (book.coverUrl && target.src === book.coverUrl) {
                  target.src = gutenbergCoverUrl;
                } else {
                  target.style.display = 'none';
                }
              }}
            />
          </div>
          <div className="book-detail-info">
            <h2 className="book-detail-title">{book.title}</h2>
            <p className="book-detail-author">by {book.author}</p>
            {book.genre && (
              <p className="book-detail-genre">{book.genre}</p>
            )}
            {book.publicationDate && (
              <p className="book-detail-publication">Published: {book.publicationDate}</p>
            )}
            {book.pageCount && (
              <p className="book-detail-page-count">{book.pageCount} pages</p>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="book-detail-tab-content">
          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div className="book-detail-progress-section">
              <h3 className="book-detail-section-title">Reading Progress</h3>
              
              {/* Status Selector */}
              <div className="book-detail-status-group">
                <label htmlFor="reading-status" className="book-detail-label">Status</label>
                <select
                  id="reading-status"
                  value={readingStatus}
                  onChange={(e) => setReadingStatus(e.target.value as any)}
                  className="book-detail-status-select"
                  disabled={saving}
                >
                  <option value="not_started">Not Started</option>
                  <option value="reading">Reading</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                  <option value="abandoned">Abandoned</option>
                </select>
              </div>

              {/* Page Inputs */}
              <div className="book-detail-page-inputs">
                <div className="book-detail-page-input-group">
                  <label htmlFor="current-page" className="book-detail-label">Current Page</label>
                  <div className="book-detail-page-controls">
                    <button
                      type="button"
                      onClick={() => adjustPage(-10)}
                      className="book-detail-page-btn"
                      disabled={saving || currentPage === 0}
                    >
                      -10
                    </button>
                    <input
                      type="number"
                      id="current-page"
                      value={currentPage || ''}
                      onChange={(e) => setCurrentPage(parseInt(e.target.value) || 0)}
                      min="0"
                      max={totalPages || undefined}
                      className="book-detail-page-input"
                      disabled={saving}
                    />
                    <button
                      type="button"
                      onClick={() => adjustPage(10)}
                      className="book-detail-page-btn"
                      disabled={saving || (totalPages > 0 && currentPage >= totalPages)}
                    >
                      +10
                    </button>
                  </div>
                </div>

                <div className="book-detail-page-input-group">
                  <label htmlFor="total-pages" className="book-detail-label">Total Pages</label>
                  <input
                    type="number"
                    id="total-pages"
                    value={totalPages || ''}
                    onChange={(e) => setTotalPages(parseInt(e.target.value) || 0)}
                    min="1"
                    className="book-detail-page-input"
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Progress Bar */}
              {totalPages > 0 && currentPage > 0 && (
                <div className="book-detail-progress-display">
                  <div className="book-detail-progress-info">
                    <span className="book-detail-progress-text">
                      Page {currentPage} / {totalPages}
                    </span>
                    <span className="book-detail-progress-percentage">
                      {Math.round(progressPercentage)}%
                    </span>
                  </div>
                  <div className="book-detail-progress-bar-container">
                    <div 
                      className="book-detail-progress-bar-fill" 
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Manual Save Button */}
              <button
                type="button"
                onClick={saveProgress}
                className="book-detail-save-btn"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
            </div>
          )}

          {/* Records Tab */}
          {activeTab === 'records' && (
            <div className="book-detail-notes-section">
              <div className="book-detail-section-header">
                <h3 className="book-detail-section-title">Reading Records</h3>
                <div className="book-detail-view-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={`book-detail-view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                  >
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('by-date')}
                    className={`book-detail-view-mode-btn ${viewMode === 'by-date' ? 'active' : ''}`}
                  >
                    By Date
                  </button>
                </div>
              </div>
              <p className="book-detail-notes-description">
                Track your daily reading sessions and see your progress over time.
              </p>

              {/* Add Record Button */}
              {!showRecordForm && (
                <button
                  type="button"
                  onClick={() => setShowRecordForm(true)}
                  className="book-detail-notes-btn"
                >
                  + Add Reading Record
                </button>
              )}

              {/* Add Record Form */}
              {showRecordForm && (
                <div className="book-detail-record-form">
                  <div className="book-detail-record-form-row">
                    <div className="book-detail-page-input-group">
                      <label htmlFor="record-date" className="book-detail-label">Date</label>
                      <input
                        type="date"
                        id="record-date"
                        value={recordDate}
                        onChange={(e) => setRecordDate(e.target.value)}
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                      />
                    </div>
                    <div className="book-detail-page-input-group">
                      <label htmlFor="record-pages" className="book-detail-label">Pages Read</label>
                      <input
                        type="number"
                        id="record-pages"
                        value={recordPagesRead || ''}
                        onChange={(e) => setRecordPagesRead(parseInt(e.target.value) || 0)}
                        min="0"
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                        required
                      />
                    </div>
                  </div>
                  <div className="book-detail-record-form-row">
                    <div className="book-detail-page-input-group">
                      <label htmlFor="record-start-page" className="book-detail-label">Start Page (optional)</label>
                      <input
                        type="number"
                        id="record-start-page"
                        value={recordStartPage}
                        onChange={(e) => setRecordStartPage(e.target.value ? parseInt(e.target.value) : '')}
                        min="0"
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                      />
                    </div>
                    <div className="book-detail-page-input-group">
                      <label htmlFor="record-end-page" className="book-detail-label">End Page (optional)</label>
                      <input
                        type="number"
                        id="record-end-page"
                        value={recordEndPage}
                        onChange={(e) => setRecordEndPage(e.target.value ? parseInt(e.target.value) : '')}
                        min="0"
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                      />
                    </div>
                    <div className="book-detail-page-input-group">
                      <label htmlFor="record-time" className="book-detail-label">Time (minutes, optional)</label>
                      <input
                        type="number"
                        id="record-time"
                        value={recordTimeMinutes}
                        onChange={(e) => setRecordTimeMinutes(e.target.value ? parseInt(e.target.value) : '')}
                        min="0"
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                      />
                    </div>
                  </div>
                  <div className="book-detail-record-form-row">
                    <div className="book-detail-page-input-group" style={{ flex: 1 }}>
                      <label htmlFor="record-notes" className="book-detail-label">Notes (optional)</label>
                      <textarea
                        id="record-notes"
                        value={recordNotes}
                        onChange={(e) => setRecordNotes(e.target.value)}
                        className="book-detail-page-input"
                        disabled={submittingRecord}
                        rows={3}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </div>
                  <div className="book-detail-record-form-actions">
                    <button
                      type="button"
                      onClick={handleAddReadingRecord}
                      className="book-detail-save-btn"
                      disabled={submittingRecord || recordPagesRead <= 0}
                    >
                      {submittingRecord ? 'Adding...' : 'Add Record'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRecordForm(false);
                        setRecordDate(new Date().toISOString().split('T')[0]);
                        setRecordPagesRead(0);
                        setRecordStartPage('');
                        setRecordEndPage('');
                        setRecordTimeMinutes('');
                        setRecordNotes('');
                      }}
                      className="book-detail-save-btn"
                      disabled={submittingRecord}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Reading Records List */}
              {loadingRecords ? (
                <div className="book-detail-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading reading records...</p>
                </div>
              ) : viewMode === 'list' ? (
                readingRecords.length > 0 ? (
                  <div className="book-detail-records-list">
                    {readingRecords.map((record) => (
                      <div key={record.id} className="book-detail-record-item">
                        <div className="book-detail-record-item-header">
                          <span className="book-detail-record-date">{formatDate(record.record_date)}</span>
                          <span className="book-detail-record-pages">{record.pages_read} pages</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteRecord(record.id)}
                            className="book-detail-record-delete"
                            title="Delete record"
                          >
                            ✕
                          </button>
                        </div>
                        {(record.start_page || record.end_page || record.reading_time_minutes || record.notes) && (
                          <div className="book-detail-record-details">
                            {record.start_page && record.end_page && (
                              <span className="book-detail-record-detail">Pages {record.start_page} - {record.end_page}</span>
                            )}
                            {record.reading_time_minutes && (
                              <span className="book-detail-record-detail">{record.reading_time_minutes} min</span>
                            )}
                            {record.notes && (
                              <p className="book-detail-record-notes">{record.notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="book-detail-empty-message">No reading records yet. Add your first reading session!</p>
                )
              ) : (
                readingRecordsByDate.length > 0 ? (
                  <div className="book-detail-records-by-date">
                    {readingRecordsByDate.map((group: any) => (
                      <div key={group.date} className="book-detail-record-date-group">
                        <div className="book-detail-record-date-header">
                          <span className="book-detail-record-date-title">{formatDate(group.date)}</span>
                          <span className="book-detail-record-date-stats">
                            {group.total_pages} pages • {group.session_count} session{group.session_count !== 1 ? 's' : ''}
                            {group.total_minutes > 0 && ` • ${group.total_minutes} min`}
                          </span>
                        </div>
                        <div className="book-detail-records-list">
                          {group.records.map((record: any) => (
                            <div key={record.id} className="book-detail-record-item">
                              <div className="book-detail-record-item-header">
                                <span className="book-detail-record-pages">{record.pages_read} pages</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRecord(record.id)}
                                  className="book-detail-record-delete"
                                  title="Delete record"
                                >
                                  ✕
                                </button>
                              </div>
                              {(record.start_page || record.end_page || record.reading_time_minutes || record.notes) && (
                                <div className="book-detail-record-details">
                                  {record.start_page && record.end_page && (
                                    <span className="book-detail-record-detail">Pages {record.start_page} - {record.end_page}</span>
                                  )}
                                  {record.reading_time_minutes && (
                                    <span className="book-detail-record-detail">{record.reading_time_minutes} min</span>
                                  )}
                                  {record.notes && (
                                    <p className="book-detail-record-notes">{record.notes}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="book-detail-empty-message">No reading records yet. Add your first reading session!</p>
                )
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="book-detail-notes-section">
              <h3 className="book-detail-section-title">Reading Notes</h3>
              <p className="book-detail-notes-description">
                Capture your thoughts, quotes, and reflections while reading this book.
              </p>
              <button
                type="button"
                onClick={handleViewNotes}
                className="book-detail-notes-btn"
                disabled={isCreatingNote}
              >
                {isCreatingNote ? 'Creating...' : '📝 View Reading Notes'}
              </button>
            </div>
          )}
        </div>

        {/* Bottom Tab Navigation */}
        <div className="book-detail-bottom-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('progress')}
            className={`book-detail-bottom-tab ${activeTab === 'progress' ? 'active' : ''}`}
          >
            <span className="book-detail-tab-icon">📊</span>
            <span className="book-detail-tab-label">Progress</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('records')}
            className={`book-detail-bottom-tab ${activeTab === 'records' ? 'active' : ''}`}
          >
            <span className="book-detail-tab-icon">📝</span>
            <span className="book-detail-tab-label">Records</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notes')}
            className={`book-detail-bottom-tab ${activeTab === 'notes' ? 'active' : ''}`}
          >
            <span className="book-detail-tab-icon">📖</span>
            <span className="book-detail-tab-label">Notes</span>
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="book-detail-error-message">
            {error}
            <button onClick={() => setError(null)} className="book-detail-error-dismiss">✕</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDetail;

